import { Injectable, signal, computed, inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ToastService } from '../toast/toast.service';
import {
  AppNotification,
  NotificationsListResponseDto,
  NotificationResponseDto,
  UnreadCountResponseDto,
  NotificationWsMessage,
  NotificationPreferenceDto,
  NotificationPreferencesResponseDto,
  NotificationPreferenceUpdateItem,
  NotificationPreferencesUpdateResponseDto,
  WebflowSyncNotifData,
} from '../models/notification.dto';
import { STORAGE_KEYS } from './storage-keys';
import { StorageService } from './storage.service';

const WS_INVALID_JWT_CODE = 4403;
const POLL_INTERVAL_MS = 30_000;
const MAX_RECONNECT_BACKOFF_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly storage = inject(StorageService);

  private readonly apiUrl = `${environment.apiHost}/api`;

  // --- State signals ---
  private readonly _notifications = signal<AppNotification[]>([]);
  private readonly _unreadCount = signal<number>(0);
  private readonly _connected = signal<boolean>(false);
  private readonly _autoOpenPending = signal(false);

  readonly notifications = this._notifications.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();
  readonly connected = this._connected.asReadonly();
  readonly autoOpenPending = this._autoOpenPending.asReadonly();

  // --- Webflow sync (toast-only, never enters the notifications list) ---
  private readonly _webflowSyncCompleted = signal<WebflowSyncNotifData | null>(null);
  private readonly _webflowSyncFailed = signal<WebflowSyncNotifData | null>(null);
  readonly webflowSyncCompleted = this._webflowSyncCompleted.asReadonly();
  readonly webflowSyncFailed = this._webflowSyncFailed.asReadonly();

  /** True when there is at least one unread notification. Drives badge visibility. */
  readonly hasUnread = computed(() => this._unreadCount() > 0);

  /** Notifications that have not been read yet (read_at === null). */
  readonly unreadNotifications = computed(() =>
    this._notifications().filter((n) => n.read_at === null)
  );

  /** Notifications that have already been read. */
  readonly readNotifications = computed(() =>
    this._notifications().filter((n) => n.read_at !== null)
  );

  // --- Init guard ---
  private isInitialized = false;
  private isNotificationsListLoaded = false;

  // --- WebSocket state ---
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // --- Polling fallback ---
  private pollIntervalId: ReturnType<typeof setInterval> | null = null;

  /**
   * Org dashboard "Activity" feed.
   * scope 'all' = workspace-wide (de-duped per event); scope 'me' = the current user's activity.
   */
  getActivity(
    scope: 'all' | 'me',
    organizationId?: string | null,
    limit = 8
  ): Observable<NotificationsListResponseDto> {
    const params: Record<string, string> = { scope, limit: String(limit) };
    if (organizationId) params['organization'] = organizationId;
    return this.http.get<NotificationsListResponseDto>(
      `${this.apiUrl}/notifications/activity/`,
      { params }
    );
  }

  // ----------------------------------------------------------------
  // Initialization
  // ----------------------------------------------------------------

  /**
   * Fetch the unread count and open the WebSocket connection. This drives the bell badge, so
   * it's called eagerly on layout load. Safe to call from multiple components — guarded by
   * isInitialized flag.
   */
  init(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;
    this.loadUnreadCount();
  }

  private loadUnreadCount(): void {
    this.http.get<UnreadCountResponseDto>(`${this.apiUrl}/notifications/unread-count/`).subscribe({
      next: (countRes) => {
        this._unreadCount.set(countRes.data.unread_count);
        if (countRes.data.unread_count > 0) this._autoOpenPending.set(true);
        this.connectWebSocket();
      },
      error: () => {
        // Initial fetch failed — start polling as fallback
        this.startPolling();
      },
    });
  }

  /**
   * Fetch the full notifications list. Deferred until the notifications panel is actually
   * opened, since the bell badge only needs the unread count from `init()`.
   */
  loadNotificationsList(): void {
    if (this.isNotificationsListLoaded) return;
    this.isNotificationsListLoaded = true;
    this.http
      .get<NotificationsListResponseDto>(`${this.apiUrl}/notifications/`, { params: { limit: '30' } })
      .subscribe({
        next: (listRes) => this._notifications.set(listRes.data.results),
        error: () => {
          this.isNotificationsListLoaded = false;
        },
      });
  }

  // ----------------------------------------------------------------
  // WebSocket
  // ----------------------------------------------------------------

  private connectWebSocket(): void {
    // From the registry rather than from `AuthService`: `AuthService` constructor-injects this
    // service, so depending on it back is a DI cycle — and it lives in `features/`, which
    // `shared/` must not import.
    const token = this.storage.getString(STORAGE_KEYS.auth.access);
    if (!token) {
      this.startPolling();
      return;
    }

    const baseWsUrl = environment.apiWsUrl ?? this.deriveWsBaseUrl();
    const url = `${baseWsUrl}/ws/notifications/?token=${encodeURIComponent(token)}`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this._connected.set(true);
      this.reconnectAttempts = 0;
      this.stopPolling();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const message: NotificationWsMessage = JSON.parse(event.data as string);
        if (message.type === 'notification.created') {
          this.handleNewNotification(message.payload);
        }
      } catch {
        // Ignore malformed messages
      }
    };

    this.ws.onclose = (event: CloseEvent) => {
      this._connected.set(false);
      this.ws = null;

      if (event.code === WS_INVALID_JWT_CODE) {
        // Invalid or expired JWT — do not retry without a fresh token
        return;
      }

      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      this._connected.set(false);
    };
  }

  private deriveWsBaseUrl(): string {
    const api = environment.apiHost ?? '';
    if (api.startsWith('https')) return api.replace('https', 'wss');
    if (api.startsWith('http')) return api.replace('http', 'ws');
    return 'ws://localhost:8000';
  }

  private scheduleReconnect(): void {
    // Exponential backoff: 1s, 2s, 4s, 8s … capped at MAX_RECONNECT_BACKOFF_MS
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), MAX_RECONNECT_BACKOFF_MS);
    this.reconnectAttempts++;

    // Use polling as a temporary fallback while reconnecting
    this.startPolling();

    this.reconnectTimeoutId = setTimeout(() => {
      this.connectWebSocket();
    }, delay);
  }

  private disconnectWebSocket(): void {
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.ws) {
      this.ws.onclose = null; // Prevent scheduleReconnect from triggering
      this.ws.close();
      this.ws = null;
    }

    this._connected.set(false);
    this.reconnectAttempts = 0;
  }

  // ----------------------------------------------------------------
  // Real-time handler
  // ----------------------------------------------------------------

  private handleNewNotification(notification: AppNotification): void {
    if (notification.type.startsWith('webflow.sync_')) {
      // Toast-only — never enters the notifications list or affects the unread count.
      this.handleWebflowSyncEvent(notification);
      return;
    }

    // Deduplicate in case the notification arrives both via REST and WS
    const already = this._notifications().some((n) => n.id === notification.id);
    if (!already) {
      this._notifications.update((list) => [notification, ...list]);
      this._unreadCount.update((c) => c + 1);
    }

    this.showToast(notification);
  }

  private handleWebflowSyncEvent(notification: AppNotification): void {
    switch (notification.type) {
      case 'webflow.sync_started':
        this.toastService.info({ message: 'Syncing Webflow sites and pages…' });
        break;
      case 'webflow.sync_completed':
        this.toastService.success({ message: 'Webflow sync completed.' });
        this._webflowSyncCompleted.set(notification.data);
        break;
      case 'webflow.sync_failed':
        this.toastService.error({ message: notification.data.error || 'Webflow sync failed.' });
        this._webflowSyncFailed.set(notification.data);
        break;
    }
  }

  private showToast(notification: AppNotification): void {
    switch (notification.type) {
      case 'request.created':
        this.toastService.info({
          title: 'New request',
          message: `"${notification.data.ticket_title}" created in ${notification.data.project_name}`,
        });
        break;
      case 'request.updated':
        this.toastService.info({
          title: 'Request updated',
          message: `"${notification.data.ticket_title}" updated in ${notification.data.project_name}`,
        });
        break;
      case 'request.deleted':
        this.toastService.info({
          message: `A request was deleted in ${notification.data.project_name}`,
        });
        break;
      case 'comment.created':
        this.toastService.info({
          title: 'New comment',
          message: `${notification.data.actor_name} commented on "${notification.data.ticket_title}"`,
        });
        break;
      case 'comment.mention':
        this.toastService.info({
          title: 'You were mentioned',
          message: `${notification.data.actor_name} mentioned you in "${notification.data.ticket_title}"`,
        });
        break;
      case 'request.review_requested':
        this.toastService.info({
          title: 'Review requested',
          message: `${notification.data.actor_name} requested your review on "${notification.data.ticket_title}"`,
        });
        break;
    }
  }

  // ----------------------------------------------------------------
  // Polling fallback
  // ----------------------------------------------------------------

  private startPolling(): void {
    if (this.pollIntervalId !== null) return;

    this.pollIntervalId = setInterval(() => {
      this.http
        .get<UnreadCountResponseDto>(`${this.apiUrl}/notifications/unread-count/`)
        .subscribe({
          next: (res) => this._unreadCount.set(res.data.unread_count),
          error: () => {
            // Ignore polling errors silently
          },
        });
    }, POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollIntervalId !== null) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }

  // ----------------------------------------------------------------
  // REST actions
  // ----------------------------------------------------------------

  /**
   * Returns the effective notification preferences for the current user.
   * Pass a projectId for project-scoped preferences; omit it for the global defaults.
   */
  getPreferences(projectId?: string): Observable<NotificationPreferenceDto[]> {
    return this.http
      .get<NotificationPreferencesResponseDto>(`${this.apiUrl}/notifications/preferences/`, {
        params: projectId ? { project: projectId } : {},
      })
      .pipe(map((res) => res.data));
  }

  /** Updates one or more notification preferences. Returns the updated items. */
  updatePreferences(updates: NotificationPreferenceUpdateItem[]): Observable<NotificationPreferenceUpdateItem[]> {
    return this.http
      .patch<NotificationPreferencesUpdateResponseDto>(`${this.apiUrl}/notifications/preferences/update/`, updates)
      .pipe(map((res) => res.data));
  }

  /** Marks one notification as read and updates local state. */
  markAsRead(id: string): void {
    const target = this._notifications().find((n) => n.id === id);
    if (!target || target.read_at !== null) return; // Already read — skip the request

    this.http
      .post<NotificationResponseDto>(`${this.apiUrl}/notifications/${id}/mark-read/`, {})
      .subscribe({
        next: (res) => {
          this._notifications.update((list) =>
            list.map((n) => (n.id === id ? { ...n, read_at: res.data.read_at } : n))
          );
          this._unreadCount.update((c) => Math.max(0, c - 1));
        },
        error: () => {
          // Non-critical — ignore silently
        },
      });
  }

  /**
   * Local-only sync after the user viewed a ticket's comment thread: the
   * comments mark-viewed endpoint already marked the ticket's unread comment
   * notifications as read server-side, so mirror that in the loaded list and
   * the badge without extra requests.
   */
  markTicketCommentNotificationsRead(ticketId: string): void {
    let cleared = 0;
    this._notifications.update((list) =>
      list.map((n) => {
        const isTicketComment =
          (n.type === 'comment.created' || n.type === 'comment.mention') &&
          n.read_at === null &&
          (n.data as { ticket_id?: string } | undefined)?.ticket_id === ticketId;
        if (!isTicketComment) return n;
        cleared++;
        return { ...n, read_at: new Date().toISOString() };
      })
    );
    if (cleared > 0) {
      this._unreadCount.update((c) => Math.max(0, c - cleared));
    }
  }

  /** Marks one notification as unread and updates local state. */
  markAsUnread(id: string): void {
    const target = this._notifications().find((n) => n.id === id);
    if (!target || target.read_at === null) return; // Already unread — skip the request

    this.http
      .post<NotificationResponseDto>(`${this.apiUrl}/notifications/${id}/mark-unread/`, {})
      .subscribe({
        next: () => {
          this._notifications.update((list) =>
            list.map((n) => (n.id === id ? { ...n, read_at: null } : n))
          );
          this._unreadCount.update((c) => c + 1);
        },
        error: () => {
          this.toastService.error({ message: 'Could not mark notification as unread.' });
        },
      });
  }

  /** Deletes one notification and updates local state. */
  deleteNotification(id: string): void {
    const target = this._notifications().find((n) => n.id === id);
    if (!target) return;
    const wasUnread = target.read_at === null;

    this.http
      .delete<{ status: string }>(`${this.apiUrl}/notifications/${id}/`)
      .subscribe({
        next: () => {
          this._notifications.update((list) => list.filter((n) => n.id !== id));
          if (wasUnread) this._unreadCount.update((c) => Math.max(0, c - 1));
        },
        error: () => {
          this.toastService.error({ message: 'Could not delete notification.' });
        },
      });
  }

  /**
   * Dismiss (soft-delete) every notification that deep-links to a given ticket.
   * Used to self-heal when a notification points to a request that no longer exists.
   */
  dismissNotificationsForTicket(ticketId: string): void {
    const matches = this._notifications().filter(
      (n) => (n.data as { ticket_id?: string })?.ticket_id === ticketId
    );
    if (matches.length === 0) return;

    for (const n of matches) {
      this.http.delete(`${this.apiUrl}/notifications/${n.id}/`).subscribe({ error: () => {} });
    }

    const removedUnread = matches.filter((n) => n.read_at === null).length;
    const ids = new Set(matches.map((n) => n.id));
    this._notifications.update((list) => list.filter((n) => !ids.has(n.id)));
    if (removedUnread > 0) {
      this._unreadCount.update((c) => Math.max(0, c - removedUnread));
    }
  }

  /** Marks all unread notifications as read and resets the counter. */
  markAllRead(): void {
    this.http
      .post<{ status: string }>(`${this.apiUrl}/notifications/mark-all-read/`, {})
      .subscribe({
        next: () => {
          const now = new Date().toISOString();
          this._notifications.update((list) =>
            list.map((n) => (n.read_at === null ? { ...n, read_at: now } : n))
          );
          this._unreadCount.set(0);
        },
        error: () => {
          this.toastService.error({ message: 'Could not mark all notifications as read.' });
        },
      });
  }

  /**
   * Navigates to the deep link for the notification and marks it as read.
   *
   * The app uses query params (not path segments) to open a specific ticket:
   *   /project/requests?projectId=<project_id>&requestId=<ticket_id>
   *
   * The projectContextGuard handles switching the active project if needed.
   * The project-requests component reads requestId to auto-open the ticket drawer.
   *
   * For request.deleted there is no ticket to open, so only projectId is passed.
   */
  navigateToDeepLink(notification: AppNotification): void {
    let queryParams: Record<string, string | undefined> | null = null;

    switch (notification.type) {
      case 'request.created':
      case 'request.updated':
        queryParams = {
          projectId: notification.data.project_id,
          requestId: notification.data.ticket_id,
        };
        break;
      case 'request.deleted':
        // Pass the ticket id too — the request is soft-deleted, so the page can still open
        // it read-only (with a Restore action) instead of just showing "it was deleted".
        queryParams = {
          projectId: notification.data.project_id,
          requestId: notification.data.ticket_id,
        };
        break;
      case 'comment.created':
      case 'comment.mention':
      case 'comment.reply':
        queryParams = {
          projectId: notification.data.project_id,
          requestId: notification.data.ticket_id,
          commentId: notification.data.comment_id,
          commentParentId: notification.data.comment_parent_id ?? undefined,
        };

        break;
      case 'task.assigned':
      case 'request.review_requested':
      case 'request.branch_deleted':
      case 'request.hold_released':
      case 'request.hubspot_message':
        queryParams = {
          projectId: notification.data.project_id,
          requestId: notification.data.ticket_id,
        };
        break;
      case 'site.published':
        // Open the Sites page on the published site's Activities tab and
        // highlight the publish entry (mirrors the comment deep-link).
        this.navigateWithReload('/sites', {
          projectId: notification.data.project_id,
          siteId: notification.data.site_id,
          activityId: notification.data.activity_id ?? undefined,
        }, 'siteId');
        this.markAsRead(notification.id);
        return;
      case 'webflow.sync_started':
      case 'webflow.sync_completed':
      case 'webflow.sync_failed':
        // Sync concerns the sites/pages content — just open the Sites page.
        this.router.navigate(['/sites']);
        this.markAsRead(notification.id);
        return;
    }

    if (queryParams) {
      this.navigateToRequestsWithReload(queryParams);
    }

    this.markAsRead(notification.id);
  }

  /**
   * Navigates to /project/requests and ensures the navigation is processed
   * even when the user is already on the exact same URL.
   *
   * Angular's default behavior is to ignore same-URL navigations, which means
   * our NavigationEnd listener in project-requests.component will not fire
   * again and the details drawer will not reopen. To work around this in a
   * localized way (without changing global router config), we do a two-step
   * navigation:
   *
   * 1) Temporarily remove the requestId query param (if present).
   * 2) Navigate again with the desired query params.
   */
  private navigateToRequestsWithReload(queryParams: Record<string, string | undefined>): void {
    this.navigateWithReload('/requests', queryParams, 'requestId');
  }

  /**
   * Same-URL-safe navigation: when already on the exact target URL, Angular
   * ignores the navigation, so temporarily drop `resetParam` and re-navigate
   * to force the page's query-param listeners to fire again.
   */
  private navigateWithReload(
    path: string,
    queryParams: Record<string, string | undefined>,
    resetParam: string
  ): void {
    const targetTree = this.router.createUrlTree([path], { queryParams });
    const targetUrl = this.router.serializeUrl(targetTree);
    const currentUrl = this.router.url;

    if (currentUrl === targetUrl && queryParams[resetParam]) {
      // Step 1: remove the param so that the next navigation is not ignored.
      this.router
        .navigate([], {
          queryParams: { [resetParam]: null },
          queryParamsHandling: 'merge',
        })
        .then(() => {
          // Step 2: navigate again with the desired query params.
          this.router.navigate([path], { queryParams });
        });
    } else {
      this.router.navigate([path], { queryParams });
    }
  }

  // ----------------------------------------------------------------
  // Cleanup
  // ----------------------------------------------------------------

  /**
   * Resets all state and disconnects the WebSocket.
   * Must be called from AuthService.logout() so the next user starts clean.
   */
  clearAutoOpen(): void {
    this._autoOpenPending.set(false);
  }

  reset(): void {
    this.disconnectWebSocket();
    this.stopPolling();
    this._notifications.set([]);
    this._unreadCount.set(0);
    this._autoOpenPending.set(false);
    this.isInitialized = false;
    this.isNotificationsListLoaded = false;
  }

  ngOnDestroy(): void {
    this.reset();
  }
}
